import {
  Grid,
  InputAdornment,
  makeStyles,
  MenuItem,
  Theme,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';
import { Editor } from '@tiptap/core';
import clsx from 'clsx';
import { Body, Heading } from 'copilot-design-system';
import {
  FormikErrors,
  FormikProps,
  FormikTouched,
  useFormikContext,
} from 'formik';
import React, { useMemo } from 'react';
import { z } from 'zod';
import { toFormikValidationSchema } from 'zod-formik-adapter';

import { PRODUCTS_PAGE } from 'src/constants';
import { AlphaNumericRegex } from 'src/constants/validationConsts';
import history from 'src/history';
import { useDefaultCurrency } from 'src/hooks/useDefaultCurrency';
import useNavigate from 'src/hooks/useNavigate';
import { useAppSelector } from 'src/hooks/useStore';
import {
  GetCurrencyPrefix,
  GetDefaultCurrency,
} from 'src/legacy/components/Formatters';
import { FormPage } from 'src/legacy/components/Page/FormPage';
import {
  BaseTextField,
  CurrencyTextField,
} from 'src/legacy/components/TextField';
import { BillingRichTextEditor } from 'src/routes/billing/components/BillingRichTextEditor';
import {
  MAX_LINE_ITEM_AMOUNT,
  MIN_LINE_ITEM_AMOUNT,
} from 'src/services/api/invoicesApi';
import {
  BILLING_PERIODS,
  PriceType,
  useAddProductMutation,
} from 'src/services/api/productsApi';
import {
  BlackHeadings,
  HoverBorder,
  NonHoverBorder,
  red,
} from 'src/theme/colors';
import { SubscriptionInterval } from 'src/types/subscriptions';
import { getPublicImageURL } from 'src/utils/ImageUtils';

import { PriceTypeToggle } from '../PriceTypeToggle';

import { ProductEditPageBreadcrumbs } from './ProductEditPageBreadcrumbs';
import { ProductFileDropzone } from './ProductFileDropzone';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    maxWidth: 700,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(5),
    margin: 'auto',
    padding: theme.spacing(5, 0),
    '& .MuiInputBase-input ': {
      fontSize: '14px',
      fontWeight: 400,
      lineHeight: '22px',
    },
  },
  title: {
    marginBottom: theme.spacing(2),
  },
  labelContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  container: {
    display: 'flex',
    alignItems: 'center',
    color: BlackHeadings,
    paddingBottom: theme.spacing(0.5),
  },
  deleteIcon: {
    border: `1px solid ${NonHoverBorder}`,
    borderRadius: theme.shape.borderRadius,
    height: theme.spacing(4.5),
    width: theme.spacing(4.5),
    display: 'flex',
    alignItems: 'center',
    boxSizing: 'border-box',
    justifyContent: 'center',
  },
  trashIcon: {
    fontSize: '20px',
    color: HoverBorder,
    '&:hover': {
      color: red,
    },
  },
  currencyField: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
  },
  addPriceButton: {
    marginTop: theme.spacing(3),
  },
  priceField: {
    gap: theme.spacing(3),
  },
  priceContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  lineHeight: {
    lineHeight: '22px',
  },
}));

export const getRenderCurrencyPrefixProps = (prefix: string) => ({
  classes: {
    input: '!pl-0 text-[14px] font-normal !h-[38px]',
  },
  startAdornment: (
    <InputAdornment position="start" className="mr-0.5">
      <span className="text-[14px] text-black-heading">{prefix}</span>
    </InputAdornment>
  ),
});

const oneTimePriceSchema = (currencyPrefix?: string) =>
  z.object({
    type: z.literal(PriceType.OneTime),
    amount: z.string({ required_error: 'Price cannot be empty' }).refine(
      (value) => {
        const number = parseFloat(value);
        return number >= MIN_LINE_ITEM_AMOUNT && number <= MAX_LINE_ITEM_AMOUNT;
      },
      {
        message: `Price must be between ${currencyPrefix}0.50 and ${currencyPrefix}100,000`,
      },
    ),
    currency: z.string(),
  });

const recurringPriceSchema = (currencyPrefix?: string) =>
  z.object({
    type: z.literal(PriceType.Recurring),
    amount: z.string({ required_error: 'Price cannot be empty' }).refine(
      (value) => {
        const number = parseFloat(value);
        return number >= MIN_LINE_ITEM_AMOUNT && number <= MAX_LINE_ITEM_AMOUNT;
      },
      {
        message: `Price must be between ${currencyPrefix}0.50 and ${currencyPrefix}100,000`,
      },
    ),
    currency: z.string(),

    interval: z.enum(
      [
        SubscriptionInterval.Day,
        SubscriptionInterval.Week,
        SubscriptionInterval.Month,
        SubscriptionInterval.Quarterly,
        SubscriptionInterval.Biannually,
        SubscriptionInterval.Year,
        SubscriptionInterval.SemiAnnually,
      ],
      { required_error: 'Billing period must be selected' },
    ),
  });

export const productSchema = (currencyPrefix?: string) =>
  z.object({
    name: z
      .string({ required_error: 'Name required' })
      .refine(
        (val: string) => AlphaNumericRegex.test(val.replaceAll(' ', '')),
        'Name cannot be only numbers',
      ),
    description: z.string().optional(),
    imageUrls: z.array(z.string()),
    prices: z.array(
      z.discriminatedUnion('type', [
        oneTimePriceSchema(currencyPrefix),
        recurringPriceSchema(currencyPrefix),
      ]),
    ),
  });

type OneTimePriceSchema = z.infer<ReturnType<typeof oneTimePriceSchema>>;
type RecurringPriceSchema = z.infer<ReturnType<typeof recurringPriceSchema>>;
export type ProductSchema = z.infer<ReturnType<typeof productSchema>>;

export const MIN_IMAGE_DIMENSION = 448;
export const MAX_IMAGE_DIMENSION = 4096;

const useForm = () => {
  const defaultCurrency = useDefaultCurrency();
  const currencyPrefix = GetCurrencyPrefix({ currency: defaultCurrency });
  const FormRenderer = ({
    touched,
    errors,
    handleBlur,
    handleChange,
    values,
    setFieldValue,
  }: FormikProps<ProductSchema>) => {
    const classes = useStyles();
    const nameInputRef = React.useRef<HTMLInputElement>(null);

    /**
     * Focus on name input when component mounts
     * autoFocus does not work when internal navigation is used
     */
    React.useEffect(() => {
      if (nameInputRef.current) {
        nameInputRef.current.focus();
      }
    }, []);

    const handleContentChange = (editor: Editor) => {
      setFieldValue('description', editor.getHTML());
    };

    const productNameHelperText = useMemo(() => {
      if (errors.name && touched.name) {
        return errors.name;
      } else {
        return (
          <Body className="text-[13px] text-gray-small">
            For optimal search visibility, keep titles less than 60 characters.
          </Body>
        );
      }
    }, [touched.name, errors.name]);

    return (
      <Grid container alignItems="flex-start" justify="center">
        <Grid item md={7}>
          <div className={classes.root}>
            <div>
              <Grid item className={classes.title}>
                <Heading className="text-xl text-black-heading ">
                  Product information
                </Heading>
              </Grid>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <BaseTextField
                    label={
                      <Heading className="text-black-heading mb-1">
                        Name
                      </Heading>
                    }
                    fullWidth
                    sizeVariant="medium"
                    name="name"
                    id="input-lineitem-name"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.name || ''}
                    variant="outlined"
                    helperText={productNameHelperText}
                    error={Boolean(touched.name && errors.name)}
                    inputRef={nameInputRef}
                    autoComplete="none"
                  />
                </Grid>
                <Grid item xs={12}>
                  <>
                    <div className="flex items-center mb-1">
                      <Heading className="text-black-heading">
                        Description
                      </Heading>
                      <Heading className=" text-gray-small">
                        &nbsp;- Optional
                      </Heading>
                    </div>
                    <div>
                      <div>
                        <BillingRichTextEditor
                          description={values.description || ''}
                          onContentChange={handleContentChange}
                        />
                      </div>
                      <Body className="text-[13px] text-gray-small text-right">
                        For optimal search visibility, keep descriptions less
                        than 155 characters.
                      </Body>
                    </div>
                  </>
                </Grid>
                <Grid item xs={12}>
                  <div className="flex items-center mb-1">
                    <Heading className="text-black-heading">Image</Heading>
                  </div>
                  <ProductFileDropzone
                    imageUrl={getPublicImageURL(
                      String(values.imageUrls || ''),
                      {
                        resize: { width: 300, height: 300 },
                      },
                    )}
                  />
                  <div className="flex justify-end">
                    <div className="flex justify-end">
                      <>
                        {errors.imageUrls ? (
                          <Body className="text-red text-[13px] font-normal">
                            {String(errors.imageUrls || '')}
                          </Body>
                        ) : (
                          <Body className=" text-[13px] text-gray-small">
                            {'Recommended at least 448x448 pixels.'}
                          </Body>
                        )}
                      </>
                    </div>
                  </div>
                </Grid>
              </Grid>
            </div>
            <PriceInfo
              defaultCurrency={defaultCurrency}
              toggleLabel="Price information"
              toggleShouldBeShown
              showRecurringField
              size="xl"
            />
          </div>
        </Grid>
      </Grid>
    );
  };

  return {
    FormRenderer,
    validationScheme: toFormikValidationSchema(productSchema(currencyPrefix)),
  };
};

export const ProductEditPage = () => {
  const [addProduct] = useAddProductMutation();
  const theme = useTheme();
  const { navigate } = useNavigate();
  const isSmallViewPort = useMediaQuery(theme.breakpoints.down('sm'));
  const accountInfo = useAppSelector((state) => state.payments.accountInfo);
  const defaultCurrency =
    accountInfo?.default_currency || GetDefaultCurrency().toLocaleLowerCase();

  const handleCancel = () => {
    navigate(PRODUCTS_PAGE.path);
  };

  const handleSaveProduct = async (values: ProductSchema) => {
    const product = await addProduct({
      product: values,
    });
    if ('data' in product) {
      history.push(`/products/details/${product.data.id}`);
    }
  };

  return (
    <FormPage
      useFormHook={useForm}
      actionLabel="Create product"
      initialFormValue={{
        name: '',
        description: '<p></p>',
        imageUrls: [],
        prices: [
          {
            amount: '',
            currency: defaultCurrency,
            type: PriceType.OneTime,
          },
        ],
      }}
      resetFormOnCancel={false}
      BreadcrumbsComponent={<ProductEditPageBreadcrumbs />}
      handleSave={handleSaveProduct}
      handleCancel={handleCancel}
      shouldShowPageAction
      lg={8}
      xs={12}
      headerVariant="bottomBorder"
      onBackButtonClick={() => history.goBack()}
      showBackButton={isSmallViewPort}
    />
  );
};

const isRecurringPriceTouched = (
  _touched: FormikTouched<OneTimePriceSchema | RecurringPriceSchema>,
  values: OneTimePriceSchema | RecurringPriceSchema | undefined,
): _touched is FormikTouched<RecurringPriceSchema> => {
  return values?.type === PriceType.Recurring;
};

const isRecurringPriceError = (
  _errors: FormikErrors<OneTimePriceSchema | RecurringPriceSchema>,
  values: OneTimePriceSchema | RecurringPriceSchema | undefined,
): _errors is FormikErrors<RecurringPriceSchema> => {
  return values?.type === PriceType.Recurring;
};

export const PriceInfo = ({
  defaultCurrency,
  showRecurringField,
  toggleLabel,
  toggleShouldBeShown,
  size,
  selectedInterval,
}: {
  defaultCurrency: string;
  showRecurringField?: boolean;
  toggleLabel?: string;
  toggleShouldBeShown?: boolean;
  size?: 'lg' | 'xl';
  /**
   * In subscription mode, once the user selects a price interval (e.g., monthly) for the first product,
   * all subsequently added products must follow the same interval. For this reason, the interval
   * dropdown is disabled to prevent changing it.
   */
  selectedInterval?: SubscriptionInterval;
}) => {
  const prefix = GetCurrencyPrefix({ currency: defaultCurrency });
  const { touched, handleBlur, handleChange, errors, values, setFieldValue } =
    useFormikContext<ProductSchema>();

  const isRecurringPrice = (
    price: OneTimePriceSchema | RecurringPriceSchema,
  ): price is RecurringPriceSchema => {
    return price?.type === PriceType.Recurring;
  };

  const getIntervalValue = (
    price: OneTimePriceSchema | RecurringPriceSchema | undefined,
  ) => {
    if (price && isRecurringPrice(price)) {
      return price.interval;
    }
    return '';
  };

  const price = values.prices.at(0);
  const priceErrorContainer = errors?.prices?.[0];
  const amountErrorHelperText =
    !!touched?.prices?.[0]?.amount &&
    typeof priceErrorContainer !== 'string' &&
    priceErrorContainer?.amount;

  const isBillingPeriodTouched =
    touched.prices &&
    touched?.prices?.[0] &&
    isRecurringPriceTouched(touched.prices[0], price) &&
    touched.prices[0].interval;

  const billingPeriodErrorHelperText =
    isBillingPeriodTouched &&
    typeof priceErrorContainer !== 'string' &&
    typeof priceErrorContainer !== 'undefined' &&
    isRecurringPriceError(priceErrorContainer, price) &&
    priceErrorContainer?.interval;

  const onToggleType = (tab: PriceType) => {
    setFieldValue('prices.[0].type', tab);
    if (tab === PriceType.Recurring) {
      setFieldValue('prices.[0].interval', SubscriptionInterval.Month);
    } else {
      setFieldValue('prices.[0].interval', undefined);
    }
  };

  return (
    <div>
      {toggleShouldBeShown && (
        <div className="flex justify-between items-center mb-4">
          <Heading size={size} className=" text-black-heading">
            {toggleLabel}
          </Heading>
          {price && (
            <PriceTypeToggle type={price.type} onToggleType={onToggleType} />
          )}
        </div>
      )}

      <div className="flex flex-col gap-4">
        <div>
          <Heading className="mb-1 text-black-heading">Price</Heading>
          <CurrencyTextField
            name="prices[0].amount"
            fullWidth
            sizeVariant="medium"
            onBlur={handleBlur}
            onChange={handleChange}
            value={price?.amount || ''}
            InputProps={getRenderCurrencyPrefixProps(prefix || '')}
            variant="outlined"
            error={Boolean(
              touched.prices?.[0]?.amount && amountErrorHelperText,
            )}
            helperText={amountErrorHelperText}
            currency={defaultCurrency}
          />
        </div>
        {showRecurringField && price?.type === PriceType.Recurring && (
          <div>
            <Heading className="text-black-heading mb-1">
              Billing period
            </Heading>
            <BaseTextField
              select
              name="prices[0].interval"
              variant="outlined"
              fullWidth
              className={clsx(
                '[&>div>svg]:h-4 [&>div>svg]:w-4 [&>div>svg]:mt-1.5 ',
                {
                  '[&>fieldset]:!border-non-hover-border':
                    Boolean(selectedInterval),
                },
              )}
              additionalSelectProps={{
                classes: {
                  root: 'text-base text-black-heading font-normal !h-[38px]',
                  disabled: 'text-hover-border',
                },
              }}
              value={getIntervalValue(price)}
              onChange={handleChange}
              onBlur={handleBlur}
              error={Boolean(
                isBillingPeriodTouched && billingPeriodErrorHelperText,
              )}
              helperText={billingPeriodErrorHelperText}
              disabled={Boolean(selectedInterval)}
            >
              {BILLING_PERIODS.map((i) => (
                <MenuItem value={i.value} key={i.value}>
                  {i.label}
                </MenuItem>
              ))}
            </BaseTextField>
          </div>
        )}
      </div>
    </div>
  );
};
